import React, { useCallback, useEffect, useRef, useState } from 'react';
import FullscreenExitOutlinedIcon from '@mui/icons-material/FullscreenExitOutlined';
import FullscreenOutlinedIcon from '@mui/icons-material/FullscreenOutlined';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

/**
 * Shows a self-contained HTML document (e.g. the Kubera client brochure) inside the docs page.
 *
 * The page keeps its own styles and print layout, so "Print / Save as PDF" prints only the
 * document (one A4 page per leaflet, light palette) without the Insights shell around it.
 * "Present" puts the document full screen for presenting from a laptop; Esc leaves.
 * On screen the document follows the app's light / dark mode through `data-theme`.
 */
const HtmlDocFrame = ({ html, title }) => {
    const theme = useTheme();
    const mode = theme.palette.mode;
    const wrapRef = useRef(null);
    const frameRef = useRef(null);
    const observerRef = useRef(null);
    const [height, setHeight] = useState(1400);
    const [presenting, setPresenting] = useState(false);

    const frameDoc = () => frameRef.current?.contentDocument || null;

    const applyTheme = useCallback(() => {
        const doc = frameDoc();
        if (doc?.documentElement) {
            doc.documentElement.setAttribute('data-theme', mode);
        }
    }, [mode]);

    const measure = useCallback(() => {
        const doc = frameDoc();
        if (doc?.documentElement) {
            setHeight(Math.ceil(doc.documentElement.scrollHeight));
        }
    }, []);

    const handleLoad = useCallback(() => {
        applyTheme();
        measure();
        observerRef.current?.disconnect();
        const doc = frameDoc();
        if (doc?.body && typeof ResizeObserver !== 'undefined') {
            observerRef.current = new ResizeObserver(measure);
            observerRef.current.observe(doc.body);
        }
    }, [applyTheme, measure]);

    useEffect(() => { applyTheme(); }, [applyTheme]);
    useEffect(() => () => observerRef.current?.disconnect(), []);

    useEffect(() => {
        const onChange = () => setPresenting(document.fullscreenElement === wrapRef.current);
        document.addEventListener('fullscreenchange', onChange);
        return () => document.removeEventListener('fullscreenchange', onChange);
    }, []);

    const togglePresent = () => {
        if (document.fullscreenElement) {
            document.exitFullscreen?.();
        } else {
            wrapRef.current?.requestFullscreen?.().catch(() => {});
        }
    };

    const print = () => {
        const win = frameRef.current?.contentWindow;
        if (win) {
            win.focus();
            win.print();
        }
    };

    return (
        <Stack spacing={1.5}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Button variant="contained" size="small" startIcon={<FullscreenOutlinedIcon />} onClick={togglePresent}>
                    Present
                </Button>
                <Button variant="outlined" size="small" startIcon={<PrintOutlinedIcon />} onClick={print}>
                    Print / Save as PDF
                </Button>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Prints as A4 pages in the light palette. In the print dialog choose &ldquo;Save as PDF&rdquo; and turn on background graphics if offered.
                </Typography>
            </Stack>
            <Box
                ref={wrapRef}
                sx={{
                    position: 'relative',
                    borderRadius: presenting ? 0 : 2,
                    overflow: 'hidden',
                    border: presenting ? 0 : '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.default',
                    height: presenting ? '100%' : 'auto',
                }}
            >
                {presenting ? (
                    <Button
                        size="small"
                        variant="contained"
                        startIcon={<FullscreenExitOutlinedIcon />}
                        onClick={togglePresent}
                        sx={{ position: 'absolute', top: 12, right: 12, zIndex: 1, opacity: 0.85 }}
                    >
                        Exit
                    </Button>
                ) : null}
                <Box
                    component="iframe"
                    ref={frameRef}
                    title={title || 'Document'}
                    srcDoc={html}
                    onLoad={handleLoad}
                    sx={{ display: 'block', width: '100%', border: 0, height: presenting ? '100%' : `${height}px` }}
                />
            </Box>
        </Stack>
    );
};

export default HtmlDocFrame;
